import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ShieldCheck, ShieldAlert, Smartphone, KeyRound, RefreshCw, Trash2, Copy, Check } from "lucide-react";
import { toast } from "sonner";

export default function Security() {
  const { refresh } = useAuth();
  const [status, setStatus] = useState({ enabled: false, recovery_codes_remaining: 0, trusted_device_count: 0 });
  const [loading, setLoading] = useState(true);

  // Setup flow state
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupStage, setSetupStage] = useState("password"); // password | scan | codes
  const [setupPassword, setSetupPassword] = useState("");
  const [setupData, setSetupData] = useState(null); // { secret, qr_data_url, otpauth_uri }
  const [setupCode, setSetupCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  // Disable flow
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");

  // Regenerate recovery codes
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenPassword, setRegenPassword] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/mfa/status");
      setStatus(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const resetSetup = () => {
    setSetupStage("password");
    setSetupPassword("");
    setSetupData(null);
    setSetupCode("");
    setRecoveryCodes([]);
    setAcknowledged(false);
    setCopied(false);
  };

  const beginSetup = async () => {
    if (!setupPassword) { toast.error("Enter your password"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/mfa/setup", { password: setupPassword });
      setSetupData(data);
      setSetupStage("scan");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start setup");
    } finally { setBusy(false); }
  };

  const confirmSetup = async () => {
    const code = setupCode.replace(/\D/g, "");
    if (code.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/mfa/verify-setup", { code });
      setRecoveryCodes(data.recovery_codes || []);
      setSetupStage("codes");
      toast.success("Two-factor authentication enabled");
      await refresh();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code");
    } finally { setBusy(false); }
  };

  const finishSetup = () => {
    setSetupOpen(false);
    resetSetup();
  };

  const copyRecovery = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Copy failed — please write them down manually"); }
  };

  const onDisable = async () => {
    if (!disablePassword || !disableCode) { toast.error("Password and code required"); return; }
    setBusy(true);
    try {
      await api.post("/mfa/disable", { password: disablePassword, code: disableCode.trim() });
      toast.success("Two-factor authentication disabled");
      setDisableOpen(false);
      setDisablePassword(""); setDisableCode("");
      await refresh();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to disable");
    } finally { setBusy(false); }
  };

  const onRegenerate = async () => {
    if (!regenPassword) { toast.error("Enter your password"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/mfa/regenerate-recovery-codes", { password: regenPassword });
      setRecoveryCodes(data.recovery_codes || []);
      setRegenOpen(false);
      setRegenPassword("");
      setSetupStage("codes");
      setSetupOpen(true); // reuse the codes view
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const onRevokeDevices = async () => {
    if (!window.confirm("Sign out of all trusted devices? You'll need to enter your MFA code on next sign-in.")) return;
    try {
      await api.post("/mfa/trusted-devices/revoke-all");
      toast.success("Trusted devices revoked");
      await load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="security-page">
      <PageHeader
        eyebrow="[07] Security"
        title="Account Security"
        subtitle="Protect your account with a second verification step. Strongly recommended for admins."
      />
      <div className="p-4 sm:p-6 lg:p-8 max-w-3xl space-y-6">
        {/* MFA status card */}
        <div className="border border-[#E5E0D5] bg-white" data-testid="mfa-status-card">
          <div className="px-6 py-5 border-b border-[#E5E0D5] flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              {status.enabled ? (
                <ShieldCheck className="w-5 h-5 text-[#2D7A4F] mt-0.5" strokeWidth={1.75} />
              ) : (
                <ShieldAlert className="w-5 h-5 text-[#B45309] mt-0.5" strokeWidth={1.75} />
              )}
              <div className="min-w-0">
                <div className="text-[#0F0F0F] font-medium">Two-factor authentication (TOTP)</div>
                <div className="text-sm text-[#5A5A5A] mt-1">
                  {status.enabled
                    ? "Active. You'll be asked for a 6-digit code each time you sign in (unless this device is trusted)."
                    : "Inactive. Add an extra layer of security to your account using an authenticator app."}
                </div>
                {status.enabled && (
                  <div className="mt-3 flex flex-wrap items-center gap-4 font-mono text-xs text-[#7A7A7A]">
                    <span data-testid="recovery-codes-count">
                      <KeyRound className="w-3 h-3 inline mr-1" />
                      {status.recovery_codes_remaining} recovery code{status.recovery_codes_remaining === 1 ? "" : "s"} remaining
                    </span>
                    <span data-testid="trusted-devices-count">
                      <Smartphone className="w-3 h-3 inline mr-1" />
                      {status.trusted_device_count} trusted device{status.trusted_device_count === 1 ? "" : "s"}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex-shrink-0">
              {!status.enabled ? (
                <Button
                  data-testid="enable-mfa-btn"
                  onClick={() => { resetSetup(); setSetupOpen(true); }}
                  disabled={loading}
                  className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase"
                >Enable</Button>
              ) : (
                <Button
                  data-testid="disable-mfa-btn"
                  variant="outline"
                  onClick={() => { setDisableOpen(true); setDisablePassword(""); setDisableCode(""); }}
                  className="rounded-none border-[#D6CFC1] bg-transparent text-[#B91C1C] hover:bg-[#FFF4EE] font-mono text-xs tracking-wider uppercase"
                >Disable</Button>
              )}
            </div>
          </div>

          {status.enabled && (
            <div className="px-6 py-4 grid sm:grid-cols-2 gap-3">
              <button
                data-testid="regenerate-codes-btn"
                onClick={() => { setRegenOpen(true); setRegenPassword(""); }}
                className="flex items-center justify-between px-4 py-3 border border-[#E5E0D5] hover:bg-[#FAF7F0] transition-colors text-left"
              >
                <div>
                  <div className="font-mono text-xs uppercase tracking-wider text-[#0F0F0F]">Regenerate recovery codes</div>
                  <div className="text-xs text-[#7A7A7A] mt-1">Replace your existing codes with a new set.</div>
                </div>
                <RefreshCw className="w-4 h-4 text-[#5A5A5A]" />
              </button>
              <button
                data-testid="revoke-devices-btn"
                onClick={onRevokeDevices}
                disabled={status.trusted_device_count === 0}
                className="flex items-center justify-between px-4 py-3 border border-[#E5E0D5] hover:bg-[#FAF7F0] transition-colors text-left disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <div>
                  <div className="font-mono text-xs uppercase tracking-wider text-[#0F0F0F]">Revoke trusted devices</div>
                  <div className="text-xs text-[#7A7A7A] mt-1">Require MFA again on all devices.</div>
                </div>
                <Trash2 className="w-4 h-4 text-[#5A5A5A]" />
              </button>
            </div>
          )}
        </div>

        {!status.enabled && (
          <div className="border border-[#E5E0D5] bg-[#FAF7F0] px-6 py-4 text-sm text-[#3A3A3A] flex items-start gap-3">
            <ShieldCheck className="w-4 h-4 text-[#5A5A5A] mt-0.5 flex-shrink-0" strokeWidth={1.75} />
            <div>
              <div className="font-medium text-[#0F0F0F] mb-1">Recommended authenticator apps</div>
              <div className="text-xs font-mono text-[#5A5A5A] leading-relaxed">
                Microsoft Authenticator · Google Authenticator · 1Password · Authy · Bitwarden
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Setup wizard */}
      <AlertDialog open={setupOpen} onOpenChange={(o) => { if (!o) { setSetupOpen(false); if (setupStage !== "codes") resetSetup(); } }}>
        <AlertDialogContent className="rounded-none bg-white border-[#D6CFC1] max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-medium tracking-tight">
              {setupStage === "password" && "Enable two-factor authentication"}
              {setupStage === "scan" && "Scan with your authenticator"}
              {setupStage === "codes" && "Save your recovery codes"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#5A5A5A] text-sm">
              {setupStage === "password" && "Confirm your password to begin setup."}
              {setupStage === "scan" && "Scan the QR code with your authenticator app, then enter the 6-digit code shown."}
              {setupStage === "codes" && "These one-time codes let you sign in if you lose your authenticator. Store them somewhere safe — they will not be shown again."}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {setupStage === "password" && (
            <div className="space-y-3 py-2">
              <Label className="uppercase-label">Current Password</Label>
              <Input
                data-testid="setup-password-input"
                type="password"
                value={setupPassword}
                onChange={(e) => setSetupPassword(e.target.value)}
                placeholder="Your current password"
                className="rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
                autoFocus
              />
            </div>
          )}

          {setupStage === "scan" && setupData && (
            <div className="space-y-4 py-2">
              <div className="flex justify-center bg-[#FAF7F0] border border-[#E5E0D5] p-4">
                <img src={setupData.qr_data_url} alt="MFA QR code" data-testid="mfa-qr" className="w-48 h-48" />
              </div>
              <div>
                <Label className="uppercase-label">Can't scan? Enter this secret manually</Label>
                <div data-testid="mfa-secret" className="mt-2 font-mono text-xs text-[#3A3A3A] bg-[#FAF7F0] border border-[#E5E0D5] px-3 py-2 break-all select-all">
                  {setupData.secret}
                </div>
              </div>
              <div>
                <Label className="uppercase-label">Enter the 6-digit code from your app</Label>
                <Input
                  data-testid="setup-code-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={setupCode}
                  onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  className="mt-2 rounded-none bg-white border-[#E5E0D5] font-mono text-lg tracking-[0.3em] text-center"
                  maxLength={6}
                />
              </div>
            </div>
          )}

          {setupStage === "codes" && (
            <div className="space-y-4 py-2">
              <div data-testid="recovery-codes-grid" className="grid grid-cols-2 gap-2 bg-[#FAF7F0] border border-[#E5E0D5] p-4">
                {recoveryCodes.map((c) => (
                  <div key={c} className="font-mono text-sm text-[#0F0F0F] select-all">{c}</div>
                ))}
              </div>
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  data-testid="copy-recovery-codes-btn"
                  onClick={copyRecovery}
                  className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-wider text-[#5A5A5A] hover:text-[#0F0F0F]"
                >
                  {copied ? <Check className="w-3 h-3 text-[#2D7A4F]" /> : <Copy className="w-3 h-3" />}
                  {copied ? "Copied" : "Copy all"}
                </button>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="ack-saved-codes"
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                    className="accent-[#1A1A1A]"
                  />
                  <span className="font-mono text-xs text-[#3A3A3A]">I have saved my recovery codes</span>
                </label>
              </div>
            </div>
          )}

          <AlertDialogFooter>
            {setupStage === "password" && (
              <>
                <AlertDialogCancel data-testid="setup-cancel-btn" className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#E8E3D6]">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="setup-continue-btn" disabled={busy} onClick={(e) => { e.preventDefault(); beginSetup(); }} className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white">Continue</AlertDialogAction>
              </>
            )}
            {setupStage === "scan" && (
              <>
                <AlertDialogCancel data-testid="setup-cancel-btn" className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#E8E3D6]">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="setup-verify-btn" disabled={busy || setupCode.length !== 6} onClick={(e) => { e.preventDefault(); confirmSetup(); }} className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white disabled:opacity-40">Verify & enable</AlertDialogAction>
              </>
            )}
            {setupStage === "codes" && (
              <AlertDialogAction
                data-testid="setup-finish-btn"
                disabled={!acknowledged}
                onClick={(e) => { e.preventDefault(); finishSetup(); }}
                className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white disabled:opacity-40"
              >Done</AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Disable confirmation */}
      <AlertDialog open={disableOpen} onOpenChange={(o) => !o && setDisableOpen(false)}>
        <AlertDialogContent className="rounded-none bg-white border-[#D6CFC1]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-medium tracking-tight">Disable two-factor authentication?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#5A5A5A]">
              Your account will be protected by password only. Confirm your password and a current code to continue.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="uppercase-label">Password</Label>
              <Input
                data-testid="disable-password-input"
                type="password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
              />
            </div>
            <div>
              <Label className="uppercase-label">6-digit code OR recovery code</Label>
              <Input
                data-testid="disable-code-input"
                type="text"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value)}
                placeholder="000000 or xxxx-xxxx-xxxx"
                className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#E8E3D6]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-disable-btn" disabled={busy} onClick={(e) => { e.preventDefault(); onDisable(); }} className="rounded-none bg-[#B91C1C] hover:bg-[#cc2200] text-white">Disable MFA</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Regenerate codes confirmation */}
      <AlertDialog open={regenOpen} onOpenChange={(o) => !o && setRegenOpen(false)}>
        <AlertDialogContent className="rounded-none bg-white border-[#D6CFC1]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-medium tracking-tight">Regenerate recovery codes?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#5A5A5A]">
              Your existing codes will stop working. Confirm your password to generate a new set.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 py-2">
            <Label className="uppercase-label">Password</Label>
            <Input
              data-testid="regen-password-input"
              type="password"
              value={regenPassword}
              onChange={(e) => setRegenPassword(e.target.value)}
              className="rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#E8E3D6]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-regen-btn" disabled={busy} onClick={(e) => { e.preventDefault(); onRegenerate(); }} className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white">Generate new codes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
